import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Copy, Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { enrollCleaningLocation } from "@/lib/enrollCleaningLocation";
import { GooglePlacesAddressInput, type PlaceData } from "./GooglePlacesAddressInput";
import { LegalEntityNipField } from "@/components/legal-entity/LegalEntityNipField";
import { HOUSING_KINDS, COMPANY_KINDS } from "@/lib/legalEntityMessages";
import { LegalEntityApiError, type LegalEntityPublic } from "@/lib/legalEntityApi";
import { useRole } from "@/contexts/RoleContext";
import { validatePIN } from "@/lib/validation";
import ValidationChecklist from "@/components/ValidationChecklist";

type WorkerFormRole = "owner" | "coordinator" | "cleaner";

function generatePIN(): string {
  // Generate random 6-digit PIN
  // Avoid simple sequences by ensuring at least some variation
  let pin = "";
  const digits = "0123456789";
  
  // Generate 6 random digits
  for (let i = 0; i < 6; i++) {
    pin += digits[Math.floor(Math.random() * digits.length)];
  }
  
  // If it's a simple sequence, regenerate (safety check)
  const validation = validatePIN(pin);
  if (!validation.allValid) {
    // Retry once more
    pin = "";
    for (let i = 0; i < 6; i++) {
      pin += digits[Math.floor(Math.random() * digits.length)];
    }
  }
  
  return pin;
}

/** Generates suggested login: first letter of first name + "." + last name (lowercase, no Polish chars). e.g. "Marek Józefiak" -> "m.jozefiak" */
function buildLoginPreview(firstName: string, lastName: string): string {
  const first = (firstName || "").trim();
  const last = (lastName || "").trim();
  if (!first || !last) return "";
  const normalize = (s: string) =>
    s
      .toLowerCase()
      .replace(/ą/g, "a")
      .replace(/ć/g, "c")
      .replace(/ę/g, "e")
      .replace(/ł/g, "l")
      .replace(/ń/g, "n")
      .replace(/ó/g, "o")
      .replace(/ś/g, "s")
      .replace(/ź|ż/g, "z")
      .replace(/\s+/g, "");
  const initial = normalize(first)[0] ?? "";
  const lastPart = normalize(last);
  return initial ? `${initial}.${lastPart}` : "";
}

function loginFromStaffEmail(email: string | null | undefined): string | null {
  if (!email) return null;
  const m = email.match(/^(.+)@staff\.domio\.com\.pl$/i);
  return m ? m[1] : null;
}

async function resolveUniqueSlug(baseSlug: string): Promise<string> {
  const normalized =
    baseSlug
      .toLowerCase()
      .replace(/[^a-z0-9.]/g, "")
      .replace(/^\.+|\.+$/g, "") || "user";

  for (let n = 0; n < 100; n++) {
    const candidate = n === 0 ? normalized : `${normalized}${n}`;
    const { data, error } = await supabase.rpc("check_slug_available", {
      p_slug: candidate,
    });
    if (error) {
      console.warn("[resolveUniqueSlug] check_slug_available:", error);
      return `${normalized}${Math.floor(Math.random() * 9000) + 1000}`;
    }
    if (data === true) return candidate;
  }
  throw new Error("Nie udało się ustalić wolnego loginu");
}

export interface NewPropertyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  onSuccess: () => void;
}

export function NewPropertyModal({
  open,
  onOpenChange,
  orgId,
  onSuccess,
}: NewPropertyModalProps) {
  const [address, setAddress] = React.useState("");
  const [googlePlaceId, setGooglePlaceId] = React.useState<string | null>(null);
  const [lat, setLat] = React.useState<number | null>(null);
  const [lng, setLng] = React.useState<number | null>(null);
  const [contractor, setContractor] = React.useState<LegalEntityPublic | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setAddress("");
      setGooglePlaceId(null);
      setLat(null);
      setLng(null);
      setContractor(null);
    }
  }, [open]);

  const handlePlaceSelect = React.useCallback((data: PlaceData) => {
    setAddress(data.address);
    setGooglePlaceId(data.google_place_id);
    setLat(data.latitude);
    setLng(data.longitude);
  }, []);

  const handleSubmit = async () => {
    if (!address.trim()) {
      toast.error("Adres jest wymagany");
      return;
    }
    
    if (!googlePlaceId) {
      toast.error("Wymagany jest wybór adresu z Google Places");
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await enrollCleaningLocation({
        orgId,
        address,
        googlePlaceId,
        latitude: lat,
        longitude: lng,
        legalEntityId: contractor?.id ?? null,
      });

      if (result.status === "duplicate") {
        toast.error(`Ta nieruchomość już istnieje w Cleaning: ${result.address}`);
        setIsSubmitting(false);
        return;
      }

      toast.success(
        result.status === "enrolled"
          ? "Nieruchomość dodana do Cleaning"
          : "Nieruchomość dodana"
      );
      if (result.contractorRecommended) {
        toast.info("Rekomendujemy dopięcie Wspólnoty lub firmy w karcie nieruchomości.");
      }
      setAddress("");
      setGooglePlaceId(null);
      setLat(null);
      setLng(null);
      setContractor(null);
      onOpenChange(false);
      onSuccess();
    } catch (e) {
      console.error("NewPropertyModal:", e);
      const errorMessage =
        e instanceof LegalEntityApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : "Błąd dodawania nieruchomości";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onPointerDownOutside={(e) => {
          const target = e.target as HTMLElement;
          if (target.closest(".pac-container")) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Nowa nieruchomość</DialogTitle>
          <DialogDescription>
            Adres jest wymagany. Dopięcie Wspólnoty lub firmy jest rekomendowane,
            ale nieobowiązkowe (np. kamienica osoby prywatnej).
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>Adres</Label>
            <GooglePlacesAddressInput
              value={address}
              onChange={setAddress}
              onPlaceSelect={handlePlaceSelect}
              placeholder="Wyszukaj adres (Google Places)"
            />
          </div>
          <LegalEntityNipField
            orgId={orgId}
            value={contractor}
            onChange={setContractor}
            allowedKinds={[...HOUSING_KINDS, ...COMPANY_KINDS]}
            flags={{ isCleaning: true }}
            optionalHint="Możesz dodać sam adres i dopiąć podmiot później."
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Anuluj
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Dodawanie...
              </>
            ) : (
              "Dodaj"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export interface NewWorkerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  onSuccess: () => void;
}

export function NewWorkerModal({
  open,
  onOpenChange,
  orgId: _orgIdProp,
  onSuccess,
}: NewWorkerModalProps) {
  const { currentRole: userRole, currentOrgId } = useRole();
  const organization = currentOrgId ? { id: currentOrgId } : null;
  const [mode, setMode] = React.useState<"hub" | "simplified">("simplified");
  const [fullName, setFullName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [loginPreview, setLoginPreview] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [selectedRole, setSelectedRole] = React.useState<WorkerFormRole>("cleaner");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Optional flag for blocking worker addition (for coordinators)
  const isAddingDisabled = false; // Can be made configurable later

  // Reset form when modal closes
  React.useEffect(() => {
    if (!open) {
      setFullName("");
      setEmail("");
      setFirstName("");
      setLastName("");
      setLoginPreview("");
      setPassword("");
      setSelectedRole("cleaner");
    }
  }, [open]);

  // Live login preview when first/last name change
  React.useEffect(() => {
    setLoginPreview(buildLoginPreview(firstName, lastName));
  }, [firstName, lastName]);

  // Set role to cleaner if user is coordinator
  React.useEffect(() => {
    if (userRole === "coordinator") {
      setSelectedRole("cleaner");
    }
  }, [userRole]);

  const handleCopy = (generatedLogin?: string) => {
    const text = generatedLogin
      ? `Login: ${generatedLogin}\nHasło: ${password}`
      : `Hasło: ${password}`;
    navigator.clipboard.writeText(text).then(
      () => toast.success("Skopiowano do schowka"),
      () => toast.error("Nie udało się skopiować")
    );
  };

  const handleGeneratePassword = () => {
    setPassword(generatePIN());
  };

  const handleSubmitHub = async () => {
    if (!email.trim()) {
      toast.error("Adres e-mail jest wymagany");
      return;
    }

    if (!organization?.id) {
      toast.error("Błąd krytyczny: Nie wykryto ID organizacji. Odśwież stronę.");
      return;
    }

    setIsSubmitting(true);
    try {
      const trimmedEmail = email.trim();
      console.log("Szukam użytkownika (RPC JSON) dla maila:", trimmedEmail);

      // Bez .maybeSingle() – funkcja SQL zwraca bezpośrednio json (lub null)
      const { data: searchResult, error: searchError } = await supabase.rpc(
        "get_profile_by_email",
        { target_email: trimmedEmail }
      );

      if (searchError) {
        console.error("BŁĄD RPC:", searchError);
        toast.error("Błąd połączenia z bazą danych.");
        return;
      }

      if (!searchResult) {
        console.warn("RPC zwróciło null - brak użytkownika.");
        toast.error("Nie znaleziono użytkownika o podanym adresie email.");
        return;
      }

      console.log("Znaleziono użytkownika:", searchResult);

      const userData = searchResult as { id: string; full_name: string | null; email: string | null };
      const existingUser = {
        id: userData.id,
        full_name: userData.full_name,
        email: userData.email,
      };

      // 2. Sprawdzamy czy już jest w organizacji
      const { data: existingMember } = await supabase
        .from("memberships")
        .select("id")
        .eq("user_id", existingUser.id)
        .eq("org_id", organization.id)
        .maybeSingle();

      if (existingMember) {
        toast.error("Ten użytkownik jest już członkiem Twojego zespołu.");
        return;
      }

      // Walidacja imienia (Frontend)
      if (!fullName || fullName.trim().length < 2) {
        toast.error("Podaj imię i nazwisko pracownika, aby utworzyć jego kartę kadrową.");
        setIsSubmitting(false);
        return;
      }

      // 3. Linkowanie z użyciem Imienia wpisanego przez Admina
      const { error: linkError } = await supabase.rpc("link_user_to_org", {
        target_user_id: existingUser.id,
        target_org_id: organization.id,
        target_role: selectedRole,
        target_full_name: fullName.trim(),
        target_email: trimmedEmail,
      });

      if (linkError) {
        console.error("Błąd linkowania (RPC):", linkError);
        throw linkError;
      }

      toast.success(`Pomyślnie dodano pracownika: ${fullName.trim()}`);
      setFullName("");
      setEmail("");
      onOpenChange(false);
      onSuccess();
    } catch (err: unknown) {
      console.error("Błąd dodawania z Huba:", err);
      const errObj = err as { message?: string; details?: string };
      const message = errObj?.message ?? errObj?.details ?? "Nieznany błąd";
      toast.error("Wystąpił błąd: " + message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitSimplified = async () => {
    if (!organization?.id) {
      toast.error("Błąd krytyczny: Nie wykryto ID organizacji. Odśwież stronę.");
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      toast.error("Imię i nazwisko są wymagane");
      return;
    }
    if (!password.trim()) {
      toast.error("Hasło (PIN) jest wymagane");
      return;
    }
    const pinValidation = validatePIN(password.trim());
    if (!pinValidation.allValid) {
      const errors: string[] = [];
      if (!pinValidation.isSixDigits) errors.push("PIN musi składać się z dokładnie 6 cyfr");
      if (!pinValidation.isNotSimple) errors.push("PIN nie może być prostą sekwencją (np. 123456, 111111)");
      toast.error("PIN nie spełnia wymagań: " + errors.join(", "));
      return;
    }
    if (userRole === "coordinator" && isAddingDisabled) {
      toast.error("Nie masz uprawnień do dodawania pracowników");
      return;
    }

    const pin = password.trim();
    const slugBase = loginPreview || buildLoginPreview(firstName, lastName);
    if (!slugBase) {
      toast.error("Nie udało się wygenerować loginu. Sprawdź imię i nazwisko.");
      return;
    }

    setIsSubmitting(true);
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error("Brak aktywnej sesji. Odśwież stronę.");
      }

      const slug = await resolveUniqueSlug(slugBase);
      const payload = {
        slug,
        pin,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        orgId: organization.id,
        role: selectedRole,
        membershipRole: selectedRole,
      };

      const { data, error } = await supabase.functions.invoke("create-worker", {
        body: payload,
      });

      const payloadError =
        data && typeof data === "object" && "error" in data
          ? String((data as { error?: string }).error ?? "")
          : "";
      if (error || payloadError) {
        throw new Error(payloadError || (error instanceof Error ? error.message : "Błąd tworzenia pracownika"));
      }

      const created = data as { userId?: string; user_id?: string; email?: string; login?: string } | null;
      const userId = created?.userId || created?.user_id;
      if (!userId) {
        throw new Error("Funkcja create-worker nie zwróciła identyfikatora użytkownika.");
      }

      const createdLogin =
        created?.login || loginFromStaffEmail(created?.email) || slug;
      toast.success(
        `Pracownik dodany!\nLOGIN: ${createdLogin}\nPIN: ${pin}`,
        { duration: 10000 }
      );
      setFirstName("");
      setLastName("");
      setLoginPreview("");
      setPassword("");
      setSelectedRole("cleaner");
      onOpenChange(false);
      onSuccess();
    } catch (err: unknown) {
      console.error("Błąd krytyczny:", err);
      const message =
        err instanceof Error
          ? err.message
          : typeof (err as { message?: string })?.message === "string"
            ? (err as { message: string }).message
            : "Wystąpił nieoczekiwany błąd";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = mode === "hub" ? handleSubmitHub : handleSubmitSimplified;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nowy pracownik</DialogTitle>
          <DialogDescription>
            Hub: dodaj istniejącego użytkownika po adresie e-mail. Uproszczone: utwórz nowe konto (imię, nazwisko, PIN).
          </DialogDescription>
        </DialogHeader>
        <Tabs value={mode} onValueChange={(v) => setMode(v as "hub" | "simplified")}>
          <TabsList>
            <TabsTrigger value="hub">Hub</TabsTrigger>
            <TabsTrigger value="simplified">Uproszczone</TabsTrigger>
          </TabsList>
          <TabsContent value="hub" forceMount className="space-y-4 pt-4 data-[state=inactive]:hidden">
            <div className="grid gap-2">
              <Label>Imię i Nazwisko</Label>
              <Input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="np. Jan Kowalski"
                disabled={isSubmitting}
              />
            </div>
            <div className="grid gap-2">
              <Label>E-mail</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@example.com"
                disabled={isSubmitting}
              />
            </div>
            <div className="grid gap-2">
              <Label>Rola</Label>
              <Select
                value={selectedRole}
                onValueChange={(value) => setSelectedRole(value as WorkerFormRole)}
                disabled={isSubmitting || userRole === "coordinator" || isAddingDisabled}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Wybierz rolę" />
                </SelectTrigger>
                <SelectContent>
                  {userRole === "owner" && (
                    <>
                      <SelectItem value="owner">Właściciel</SelectItem>
                      <SelectItem value="coordinator">Koordynator</SelectItem>
                      <SelectItem value="cleaner">Pracownik</SelectItem>
                    </>
                  )}
                  {userRole === "coordinator" && (
                    <SelectItem value="cleaner">Pracownik</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </TabsContent>
          <TabsContent value="simplified" forceMount className="space-y-4 pt-4 data-[state=inactive]:hidden">
            <div className="grid gap-2">
              <Label>Imię</Label>
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Jan"
                disabled={isSubmitting}
              />
            </div>
            <div className="grid gap-2">
              <Label>Nazwisko</Label>
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Kowalski"
                disabled={isSubmitting}
              />
            </div>
            <div className="grid gap-2">
              <Label>Sugerowany Login</Label>
              <Input
                value={loginPreview}
                readOnly
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">
                Login może otrzymać przyrostek liczbowy (np. m.jozefiak1), jeśli nazwa jest już zajęta.
              </p>
            </div>
            <div className="grid gap-2">
              <Label>Rola</Label>
              <Select
                value={selectedRole}
                onValueChange={(value) => setSelectedRole(value as WorkerFormRole)}
                disabled={isSubmitting || userRole === "coordinator" || isAddingDisabled}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Wybierz rolę" />
                </SelectTrigger>
                <SelectContent>
                  {userRole === "owner" && (
                    <>
                      <SelectItem value="owner">Właściciel</SelectItem>
                      <SelectItem value="coordinator">Koordynator</SelectItem>
                      <SelectItem value="cleaner">Pracownik</SelectItem>
                    </>
                  )}
                  {userRole === "coordinator" && (
                    <SelectItem value="cleaner">Pracownik</SelectItem>
                  )}
                </SelectContent>
              </Select>
              {userRole === "coordinator" && (
                <p className="text-sm text-muted-foreground">
                  Koordynatorzy mogą dodawać tylko pracowników
                </p>
              )}
            </div>
            <div className="grid gap-2">
              <Label>Hasło (PIN)</Label>
              <div className="flex gap-2">
                <Input 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Wpisz PIN lub wygeneruj"
                  disabled={isSubmitting}
                  maxLength={6}
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  size="icon" 
                  onClick={handleGeneratePassword} 
                  title="Generuj PIN"
                  disabled={isSubmitting}
                >
                  <Wand2 className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => handleCopy()}
                  title="Kopiuj PIN do schowka"
                  disabled={isSubmitting || !password}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <ValidationChecklist value={password} type="pin" />
            </div>
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Anuluj
          </Button>
          {mode === "simplified" ? (
            <>
              <Button variant="outline" onClick={() => handleCopy()} disabled={isSubmitting || !password}>
                <Copy className="mr-2 h-4 w-4" />
                Kopiuj PIN
              </Button>
              <Button
                onClick={handleSubmitSimplified}
                disabled={
                  isSubmitting ||
                  !firstName.trim() ||
                  !lastName.trim() ||
                  !password.trim() ||
                  !validatePIN(password.trim()).allValid ||
                  (userRole === "coordinator" && isAddingDisabled)
                }
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Zapisywanie...
                  </>
                ) : (
                  "Zapisz"
                )}
              </Button>
            </>
          ) : (
            <Button onClick={handleSubmit} disabled={isSubmitting || !email.trim()}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Zapisywanie...
                </>
              ) : (
                "Dodaj do zespołu"
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export interface OneTimeTaskModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  locations: { id: string; address: string }[];
  sectionsByLocation: Record<string, { id: string; name: string; assigned_staff_id: string | null }[]>;
  onSuccess: () => void;
}

export function OneTimeTaskModal({
  open,
  onOpenChange,
  orgId,
  locations,
  sectionsByLocation,
  onSuccess,
}: OneTimeTaskModalProps) {
  const [locationId, setLocationId] = React.useState<string>("");
  const [sectionId, setSectionId] = React.useState<string>("");
  const [taskName, setTaskName] = React.useState("");
  const [requiresPhoto, setRequiresPhoto] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleSubmit = async () => {
    if (!locationId || !sectionId || !taskName.trim()) return;

    setIsSubmitting(true);
    try {
      // 1. Dodaj zadanie jednorazowe - dostępne od razu (dzisiaj)
      // Inicjalizuj checklist jako tablicę z jednym obiektem dla spójności z widokiem pracownika
      const checklistItem = {
        id: crypto.randomUUID(),
        name: taskName,
        completed: false,
        requires_photo: requiresPhoto,
        section_id: sectionId,
      };
      
      const { data: taskData, error: taskError } = await supabase
        .from("cleaning_tasks")
        .insert({
          org_id: orgId,
          location_id: locationId,
          section_id: sectionId,
          coordinator_notes: taskName, // Nazwa zadania w coordinator_notes
          task_type: "coordinator_single", // Typ zadania jednorazowego
          status: "pending",
          assigned_staff_id: null,
          scheduled_at: new Date().toISOString(), // Pełny timestamp - dostępne od razu
          checklist: [checklistItem],
        })
        .select()
        .single();

      if (taskError) throw taskError;

      toast.success("Zadanie zostało dodane");
      onSuccess?.();
      onOpenChange(false);
      
      // Reset formularza
      setTaskName("");
      setRequiresPhoto(false);
    } catch (error: any) {
      console.error("Error creating task:", error);
      toast.error("Nie udało się dodać zadania");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Nowe zadanie jednorazowe</DialogTitle>
          <DialogDescription>
            Dodaj szybkie zadanie dla konkretnego obszaru.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>Lokalizacja</Label>
            <Select value={locationId} onValueChange={setLocationId}>
              <SelectTrigger>
                <SelectValue placeholder="Wybierz lokalizację" />
              </SelectTrigger>
              <SelectContent>
                {locations.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>
                    {loc.address}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>Obszar</Label>
            <Select
              value={sectionId}
              onValueChange={setSectionId}
              disabled={!locationId}
            >
              <SelectTrigger>
                <SelectValue placeholder="Wybierz obszar" />
              </SelectTrigger>
              <SelectContent>
                {(sectionsByLocation[locationId] || []).filter(s => s.id).map((section) => (
                  <SelectItem key={section.id} value={section.id}>
                    {section.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label>Nazwa zadania</Label>
            <Input
              value={taskName}
              onChange={(e) => setTaskName(e.target.value)}
              placeholder="np. Mycie okien"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="requires-photo"
              checked={requiresPhoto}
              onCheckedChange={(checked) => setRequiresPhoto(checked === true)}
            />
            <Label
              htmlFor="requires-photo"
              className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Wymagaj zdjęcia przy zakończeniu
            </Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Anuluj
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || !locationId || !sectionId || !taskName.trim()}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Dodawanie...
              </>
            ) : (
              "Dodaj"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}